import { z } from 'zod';
import { MfaFactor } from '../foundations/jsonb-types/sign-in-experience.js';
/**
 * The key for MFA-related data in user's logto_config
 */
export const userMfaDataKey = 'mfa';
/*
 * The key for passkey sign-in data in user's logto_config
 */
export const userPasskeySignInDataKey = 'passkey_sign_in';
/**
 * Schema for MFA-related data stored in user's logto_config
 */
export const userMfaDataGuard = z.object({
    /**
     * Whether the user has actively enabled/bound MFA factors
     *
     * Note: The `undefined` value indicates that a new user has never made a choice on enabling the optional MFA; or an
     * existing user data was created before the introduction of this field, so the MFA enabled state is unknown. We need
     * to check extra conditions to determine it when the user submits the experience interaction.
     * @see {@link @logto/core/packages/core/src/routes/experience/classes/mfa.ts#assertOptionalMfaEnablement}
     */
    enabled: z.boolean().optional(),
    /**
     * Whether the user has skipped MFA binding flow
     */
    skipped: z.boolean().optional(),
    /**
     * Whether the user has skipped optional additional MFA binding suggestion
     */
    additionalBindingSuggestionSkipped: z.boolean().optional(),
    /**
     * Whether the user has skipped MFA verification on sign-in
     *
     * Users can manually disable MFA verification requirement for sign-in,
     * but if the MFA policy is set to mandatory, this setting will be ignored.
     */
    skipMfaOnSignIn: z.boolean().optional(),
    /**
     * Implicit Email/Phone MFA factors the user has explicitly opted into via "Add".
     *
     * Email/Phone MFA has no bind record of its own: it would otherwise be derivable implicitly
     * from the user's `primaryEmail`/`primaryPhone` whenever the sign-in experience enables the
     * matching factor, but that alone is not enough to count as MFA the user actually chose —
     * virtually every user has an email. Listing a factor here is what makes it count as bound for
     * this user, without touching the user's primary email/phone identifier or the tenant-wide
     * sign-in experience setting. Absent or empty means the user has not opted in.
     */
    enabledImplicitMfaFactors: z
        .array(z.union([
        z.literal(MfaFactor.EmailVerificationCode),
        z.literal(MfaFactor.PhoneVerificationCode),
    ]))
        .optional(),
});
/**
 * Schema for passkey sign-in related data stored in user's logto_config
 */
export const userPasskeySignInDataGuard = z.object({
    /**
     * Whether the user has skipped binding passkey for sign-in persistently
     */
    skipped: z.boolean().optional(),
});
/**
 * Schema for the MFA settings API response (GET/PATCH /api/my-account/mfa-settings)
 */
export const userMfaSettingsResponseGuard = z.object({
    skipMfaOnSignIn: z.boolean(),
});
/**
 * Schema for user's logto_config field
 */
export const userLogtoConfigGuard = z.object({
    [userMfaDataKey]: userMfaDataGuard.optional(),
    [userPasskeySignInDataKey]: userPasskeySignInDataGuard.optional(),
});
